import React, { FC, useEffect, useState } from 'react'

import {
  PLAN_V3_CATEGORIES,
  getPlanCategoryLabel,
  SIM_JOURNEY_TYPES,
} from '@smarty-nx/code-utils'
import {
  getAllPlansPageCarouselSection,
  getPlansSlugs,
  planCollectionNames,
} from '@smarty-nx/contentful'
import cn from 'classnames'

import {
  DataOnlyInfoModal,
  SocialTariffInfoModal,
  VoiceInfoModal,
} from './_parts'
import classes from './plan-batches.module.scss'
import { FEATURES } from '../../../config'
import { useBuildTimePlans, usePlansWithFallback } from '../../../hooks'
import { safeLocation } from '../../../utils/safeWindow'
import { PlansCarousel, PlanTilesBatch, SmartyTabs } from '../../components'

const VOICE_PLAN_SLUGS = getPlansSlugs(planCollectionNames.VOICE_PLANS)
const DATA_ONLY_PLAN_SLUGS = getPlansSlugs(planCollectionNames.DATA_ONLY_PLANS)
const SOCIAL_TARIFF_PLAN_SLUGS = getPlansSlugs(planCollectionNames.SOCIAL_PLANS)

const classicPlanCategoryLabel = getPlanCategoryLabel(
  PLAN_V3_CATEGORIES.CLASSIC
)

const tabs = [
  {
    hash: '#all',
    content: 'All plans',
    itemName: 'all_plans_tab',
  },
  {
    hash: '#classic',
    content: `${classicPlanCategoryLabel}`,
    itemName: 'voice_tab',
  },
  {
    hash: '#data-only',
    content: 'Data Only plans',
    itemName: 'data_only_tab',
  },
]

if (FEATURES.SOCIAL_TARIFF_TO_ALL_PLANS) {
  tabs.push({
    hash: '#social-tariff',
    content: 'Social Tariff plans',
    itemName: 'social_tariff_tab',
  })
}

const section = getAllPlansPageCarouselSection()

const defaultPosition =
  section && section.selectedItem
    ? section.items.findIndex((item) => item.slug === section.selectedItem.slug)
    : 0

export const PlanBatchesSection: FC = () => {
  const buildTimePlans = useBuildTimePlans()

  const { list: voicePlans } = usePlansWithFallback(
    VOICE_PLAN_SLUGS,
    buildTimePlans
  )
  const { list: dataOnlyPlans } = usePlansWithFallback(
    DATA_ONLY_PLAN_SLUGS,
    buildTimePlans
  )
  const { list: socialTariffPlans } = usePlansWithFallback(
    SOCIAL_TARIFF_PLAN_SLUGS,
    buildTimePlans
  )

  // window.location.hash is readable the instant client JS runs — before
  // hydration — so reading it unconditionally would make the client's first
  // render diverge from the 'all'-only static HTML and trigger a hydration
  // mismatch (React discards and client-renders the subtree, which is what
  // caused the old flash to be a race rather than a fix). Staying on 'all'
  // until mount guarantees the first client render matches the server, then
  // a single, ordinary re-render picks up the real hash once it's safe to.
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => setHasMounted(true), [])

  const location = safeLocation()
  const filter =
    hasMounted && location.hash ? location.hash.replace('#', '') : 'all'
  const slugs = section?.items.map((item) => item.slug)
  const { list: currentOffersList } = usePlansWithFallback(
    slugs || [],
    buildTimePlans
  )

  return (
    <div className={cn(classes.root, { [classes.ready]: hasMounted })}>
      {/* TABS does not work, copied to have good UI */}
      <SmartyTabs tabs={tabs} activeHash={`#${filter}`} />

      {section && slugs && (filter === 'all' || filter === '') && (
        <div
          data-module-id="current-offers"
          data-module_name="current_offers"
          data-module_placement="plan-list"
          data-order-type={SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY}
        >
          <PlansCarousel
            title={section.title}
            list={currentOffersList}
            heading={<div className={classes.spacer} />}
            dataTestId="current-offers"
            moduleId="current-offers"
            defaultPosition={defaultPosition}
          />
        </div>
      )}

      <div className={cn('redesign-container', classes.container)}>
        {(filter === 'classic' || filter === 'all' || filter === '') && (
          <PlanTilesBatch
            dataTestId="voice-plans"
            moduleId="voice-plans"
            moduleName="voice_plans"
            orderType={SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY}
            title={`${classicPlanCategoryLabel}`}
            plans={voicePlans}
            subtitle="Unlimited calls & texts. eSIM available"
            info={<VoiceInfoModal />}
            className={cn(classes.plans, classes.noMarginOnMobile)}
          />
        )}

        {(filter === 'data-only' || filter === 'all' || filter === '') && (
          <PlanTilesBatch
            dataTestId="data-only-plans"
            moduleId="data-only-plans"
            moduleName="data_only_plans"
            orderType={SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY}
            title="Data Only plans"
            plans={dataOnlyPlans}
            subtitle="Just great value data. eSIM available"
            info={<DataOnlyInfoModal />}
            className={classes.plans}
          />
        )}

        {(filter === 'social-tariff' || filter === 'all' || filter === '') &&
          FEATURES.SOCIAL_TARIFF_TO_ALL_PLANS && (
            <PlanTilesBatch
              dataTestId="social-tariff-plans"
              moduleId="social-tariff-plans"
              moduleName="social_tariff_plans"
              orderType={SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY}
              title="Social Tariff plans"
              plans={socialTariffPlans}
              subtitle="Low-cost unlimited plan for those who need it most (eligibility checks required)"
              info={<SocialTariffInfoModal />}
              className={classes.plans}
            />
          )}
      </div>
    </div>
  )
}
